import {
  Button,
  IconButton,
  Icons,
  Input,
  Label,
  LoadingIndicator,
} from '@commenda/kakashi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@commenda/kakashi/src/components/Dropdown/_base';
import { FormsTemplateStatus, FormsTemplateType } from '@commenda/types';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useFormEditor } from '../hooks/useFormEditor';
import { EditableKeyCell } from '../templates/EditableKeyCell';
import { EditableStatusCell } from '../templates/EditableStatusCell';
import { EditTemplateModal } from '../templates/EditTemplateModal';
import type { FormDSL } from '@commenda/types';

type FormBuilderHeaderProps = {
  formBuilder: ReturnType<
    (typeof import('../hooks/useFormBuilder'))['useFormBuilder']
  >;
  templateId?: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleImport: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleQuickSave: () => void;
  handleGoToTemplates: () => void;
  handleGoToKeys: () => void;
  handleReset: () => void;
  getStatusBadgeVariant: (
    status: FormsTemplateStatus
  ) => 'success' | 'warning' | 'gray';
};

// Helper function to count all components in the DSL
function getTotalComponentCount(dsl: FormDSL): number {
  return dsl.steps.reduce((total, step) => {
    return (
      total +
      step.modules.reduce((moduleTotal, module) => {
        return moduleTotal + module.components.length;
      }, 0)
    );
  }, 0);
}

function FormBuilderHeader({
  formBuilder,
  templateId,
  fileInputRef,
  handleImport,
  handleQuickSave,
  handleGoToTemplates,
  handleGoToKeys,
  handleReset,
  getStatusBadgeVariant,
}: FormBuilderHeaderProps) {
  const router = useRouter();
  const {
    devMode,
    setDevMode,
    readOnly,
    getVersionsHref,
    getPreviewHref,
    showKeyManagement,
  } = useFormEditor();
  const formKey = formBuilder.templateMetadata?.key;
  const formKeyName = formBuilder.templateMetadata?.keyName;
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  return (
    <div className="flex items-center justify-between align-middle px-4 py-3 bg-bg-primary border-b border-border-secondary">
      <div className="flex items-center justify-center gap-4">
        <div className="flex items-center justify-center gap-3">
          <div className="flex items-center gap-2">
            <IconButton
              icon={<Icons.ChevronLeft />}
              aria-label="Back"
              size="sm"
              onClick={() =>
                router.push(
                  getVersionsHref(formBuilder.templateMetadata?.name ?? '')
                )
              }
            />
            <span className="txt-large-medium text-text-primary">
              {formBuilder.templateMetadata?.name || 'Editor'}
            </span>
            {formBuilder.templateMetadata?.version && (
              <span className="text-[12px] mt-[2px] font-normal text-text-tertiary">
                v{formBuilder.templateMetadata.version}
              </span>
            )}
          </div>
          {/* {formBuilder.templateMetadata?.status && (
            <Badge
              size="sm"
              variant={getStatusBadgeVariant(
                formBuilder.templateMetadata.status as FormsTemplateStatus
              )}
            >
              {formBuilder.templateMetadata.status}
            </Badge>
          )} */}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {formBuilder.templateMetadata?.id && (
          <div className="flex items-center gap-2 justify-center whitespace-nowrap">
            <div className="w-px h-6 bg-border-secondary" />
            <div className="flex items-center gap-1">
              <span className="txt-xs-regular text-text-tertiary">
                Status :
              </span>
              <EditableStatusCell
                id={formBuilder.templateMetadata?.id}
                status={
                  formBuilder.templateMetadata?.status as FormsTemplateStatus
                }
                onUpdate={(id: string, newStatus: FormsTemplateStatus) => {
                  formBuilder.updateTemplateMetadata({
                    id,
                    status: newStatus,
                  });
                }}
              />
            </div>

            {showKeyManagement && (
              <div className="flex items-center gap-2">
                <Label htmlFor="quickKey" className="txt-xs-regular">
                  Key :
                </Label>
                <EditableKeyCell
                  // add label and value to the key cell
                  template={{
                    key: formKey,
                    keyName: formKeyName,
                    id: templateId,
                  }}
                  onUpdate={async (
                    id: string,
                    newKey: string | null,
                    newKeyName: string | null
                  ) => {
                    await formBuilder.updateTemplateMetadata({
                      id,
                      key: newKey,
                      keyName: newKeyName,
                    });
                  }}
                />
              </div>
            )}
          </div>
        )}
        <div className="w-px h-6 bg-border-secondary" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="tertiary" size="sm">
              <Icons.DotsVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {!readOnly && showKeyManagement && (
              <DropdownMenuItem>
                <div
                  className="cursor-pointer txt-small-medium text-text-secondary flex items-center gap-1"
                  onClick={() => setShowEditTemplateModal(true)}
                >
                  <Icons.Edit01 className="w-4 h-4 mr-1" />
                  Edit Key
                </div>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem>
              <div
                className="cursor-pointer txt-small-medium text-text-secondary flex items-center gap-1"
                onClick={handleGoToTemplates}
              >
                <Icons.List className="w-4 h-4 mr-1" />
                Templates
              </div>
            </DropdownMenuItem>
            {showKeyManagement && (
              <DropdownMenuItem>
                <div
                  className="cursor-pointer txt-small-medium text-text-secondary flex items-center gap-1"
                  onClick={handleGoToKeys}
                >
                  <Icons.Key01 className="w-4 h-4 mr-1" />
                  Keys
                </div>
              </DropdownMenuItem>
            )}
            {!readOnly && (
              <DropdownMenuItem>
                <div
                  className="cursor-pointer txt-small-medium text-text-secondary flex items-center gap-1"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    await fileInputRef.current?.click();
                  }}
                >
                  <Icons.Upload01 className="w-4 h-4 mr-1" />
                  Import
                </div>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem>
              <div
                className="cursor-pointer txt-small-medium text-text-secondary flex items-center gap-1"
                onClick={() => {
                  formBuilder.exportToJSON();
                }}
              >
                <Icons.Download01 className="w-4 h-4 mr-1" />
                Export JSON
              </div>
            </DropdownMenuItem>
            {!readOnly && (
              <DropdownMenuItem>
                <div
                  className="cursor-pointer txt-small-medium text-text-secondary flex items-center gap-1"
                  onClick={() => {
                    handleReset();
                  }}
                >
                  <Icons.RefreshCcw01 className="w-4 h-4 mr-1" />
                  Reset
                </div>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {!readOnly && (
          <>
            <Input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleImport(e)}
              className="hidden"
            />

            <Button
              variant="tertiary"
              size="sm"
              onClick={handleQuickSave}
              disabled={
                formBuilder.isSaving ||
                getTotalComponentCount(formBuilder.dsl) === 0 ||
                !(formBuilder.isDirty && !formBuilder.isSaving)
              }
            >
              {formBuilder.isSaving ? (
                <LoadingIndicator
                  size={16}
                  _className="min-w-[58px] max-w-[58px] min-h-[20px] max-h-[20px]"
                />
              ) : formBuilder.templateMetadata?.id ? (
                <>
                  <Icons.Save01 className="w-4 h-4 mr-1" />
                  Save
                </>
              ) : (
                <>
                  <Icons.Save01 className="w-4 h-4 mr-1" />
                  Save Template
                </>
              )}
            </Button>
          </>
        )}
        <div className="w-px h-6 bg-border-secondary" />
        <Button
          variant="primary"
          size="sm"
          disabled={
            !templateId ||
            !(
              formBuilder.isSaving ||
              getTotalComponentCount(formBuilder.dsl) === 0 ||
              !(formBuilder.isDirty && !formBuilder.isSaving)
            )
          }
          onClick={() => {
            if (!templateId) return;
            router.push(getPreviewHref(templateId));
          }}
        >
          <Icons.Eye className="w-4 h-4 mr-1" />
          Preview
        </Button>
      </div>
      <EditTemplateModal
        open={showEditTemplateModal}
        onOpenChange={setShowEditTemplateModal}
        template={
          templateId
            ? {
                id: templateId,
                key: formKey ?? null,
                keyName: formKeyName ?? null,
                name: formBuilder.templateMetadata?.name || '',
                description: formBuilder.templateMetadata?.description || null,
                status:
                  (formBuilder.templateMetadata
                    ?.status as FormsTemplateStatus) ||
                  FormsTemplateStatus.DRAFT,
                version: formBuilder.templateMetadata?.version || '',
                versionNote: formBuilder.templateMetadata?.versionNote || null,
                dsl: formBuilder.dsl,
                type: FormsTemplateType.FORM_TEMPLATE,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              }
            : null
        }
        onSuccess={() => {
          // Modal updates the API; page refresh will show updated metadata
        }}
        onNavigateToEditor={() => {
          setShowEditTemplateModal(false);
        }}
      />
    </div>
  );
}

export default FormBuilderHeader;
